// frontend/src/features/portfolio/components/PortfolioSummaryView.jsx
import React, { useRef, useState } from 'react';

const LOW_COVERAGE = 0.6;
const CLICK_DELAY_MS = 250;

const fmtUSD = (n) =>
  n == null ? '-' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtPct = (n) => (n == null ? '-' : `${n.toFixed(2)}%`);
const fmtNum = (n, d = 2) => (n == null ? '-' : n.toFixed(d));
const cov = (m) => (m && m.coverage_mv_pct != null ? `${Math.round(m.coverage_mv_pct * 100)}% of MV` : '');
const isLimited = (m) => m && m.coverage_mv_pct != null && m.coverage_mv_pct < LOW_COVERAGE;

// Single vs double click discrimination per the shared contract: detail===1
// schedules the single-click action after 250ms; detail>=2 cancels that timer
// and runs the double-click action (undoing the native dblclick text selection).
function Metric({ label, value, meta, onFieldClick, onFieldDoubleClick }) {
  const timerRef = useRef(null);
  const [hover, setHover] = useState(false);
  // A field is inspectable only when the page wired click handlers; only those
  // get the pointer cursor + hover underline affordance.
  const clickable = !!(onFieldClick || onFieldDoubleClick);
  const handleClick = (e) => {
    if (e.detail >= 2) {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      window.getSelection().removeAllRanges();
      onFieldDoubleClick && onFieldDoubleClick();
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onFieldClick && onFieldClick();
    }, CLICK_DELAY_MS);
  };
  return (
    <div
      style={{ minWidth: 120, cursor: clickable ? 'pointer' : 'default' }}
      onClick={handleClick}
      onMouseEnter={() => clickable && setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={{ fontSize: 13, opacity: 0.6 }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 500, opacity: isLimited(meta) ? 0.45 : 1, textDecoration: hover ? 'underline' : 'none' }}>{value}</div>
      <div style={{ fontSize: 12, opacity: 0.5 }}>
        {isLimited(meta) ? 'limited data' : cov(meta)}
      </div>
    </div>
  );
}

export default function PortfolioSummaryView({ data, onField }) {
  const { name, codename, accounts = [], as_of_date, inception_date, has_snapshot } = data;
  // Yields are standardized to percent in the lake (post 2026-06-01 seam patch);
  // this toggle lets the desk show/hide the Avg YTW / YTM cards. Defaults on.
  const [showYields, setShowYields] = useState(true);

  // Build the FieldContext payload for one metric and hand it up (single vs
  // double is decided in Metric; onField signals which). No-op when the page
  // wires no handler (onField absent), leaving the cards inert as before.
  const fieldHandlers = (label, valueStr, rawValue) =>
    onField
      ? {
          onFieldClick: () => onField({ field_label: label, field_value: valueStr, field_value_raw: rawValue }, false),
          onFieldDoubleClick: () => onField({ field_label: label, field_value: valueStr, field_value_raw: rawValue }, true),
        }
      : {};
  const rawStr = (n) => (n == null ? null : String(n));

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <h1 className="font-display" style={{ fontSize: 'clamp(1.5rem,3vw,2.25rem)', fontWeight: 400, margin: 0 }}>
        {name}
      </h1>
      <div style={{ fontSize: 14, opacity: 0.6, marginTop: 6 }}>
        {codename ? `${codename} · ` : ''}{accounts.join(', ') || 'No accounts'}
        {inception_date ? ` · since ${inception_date}` : ''}
        {as_of_date ? ` · as of ${as_of_date}` : ''}
      </div>

      {!has_snapshot ? (
        <p style={{ marginTop: 24, opacity: 0.6 }}>No current holdings snapshot for this portfolio.</p>
      ) : (
        <>
          <section style={{ display: 'flex', gap: 24, marginTop: 24, flexWrap: 'wrap' }}>
            <Metric label="Par Amount" value={fmtUSD(data.overview.par_amount)}
              {...fieldHandlers('Par Amount', fmtUSD(data.overview.par_amount), rawStr(data.overview.par_amount))} />
            <Metric label="Market Value" value={fmtUSD(data.overview.market_value)}
              {...fieldHandlers('Market Value', fmtUSD(data.overview.market_value), rawStr(data.overview.market_value))} />
            <Metric label="Cash Balance" value={fmtUSD(data.overview.cash_balance)}
              {...fieldHandlers('Cash Balance', fmtUSD(data.overview.cash_balance), rawStr(data.overview.cash_balance))} />
          </section>

          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 24, fontSize: 13, opacity: 0.7, cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={showYields} onChange={(e) => setShowYields(e.target.checked)} />
            Show yields (YTW / YTM)
          </label>
          <section style={{ display: 'flex', gap: 24, marginTop: 12, flexWrap: 'wrap' }}>
            {showYields && (
              <>
                <Metric label="Avg Yield to Worst" value={fmtPct(data.metrics.ytw.value)} meta={data.metrics.ytw}
                  {...fieldHandlers('Avg Yield to Worst', fmtPct(data.metrics.ytw.value), rawStr(data.metrics.ytw.value))} />
                {/* On-cost to stated maturity - the FI Distribution report's
                    "Average YTM (to maturity, on cost)" parity number. */}
                <Metric label="Avg YTM (on cost)" value={fmtPct(data.metrics.ytm.value)} meta={data.metrics.ytm}
                  {...fieldHandlers('Avg YTM (on cost)', fmtPct(data.metrics.ytm.value), rawStr(data.metrics.ytm.value))} />
              </>
            )}
            <Metric label="Avg Maturity (yrs)" value={fmtNum(data.metrics.effective_maturity_yrs.value)} meta={data.metrics.effective_maturity_yrs}
              {...fieldHandlers('Avg Maturity (yrs)', fmtNum(data.metrics.effective_maturity_yrs.value), rawStr(data.metrics.effective_maturity_yrs.value))} />
            <Metric label="Avg Duration (worst)" value={fmtNum(data.metrics.duration_to_worst.value)} meta={data.metrics.duration_to_worst}
              {...fieldHandlers('Avg Duration (worst)', fmtNum(data.metrics.duration_to_worst.value), rawStr(data.metrics.duration_to_worst.value))} />
            <Metric label="Avg Duration" value={fmtNum(data.metrics.duration.value)} meta={data.metrics.duration}
              {...fieldHandlers('Avg Duration', fmtNum(data.metrics.duration.value), rawStr(data.metrics.duration.value))} />
            <Metric label="Avg Rating S&P" value={data.metrics.rating_sp.value || 'NR'} meta={data.metrics.rating_sp}
              {...fieldHandlers('Avg Rating S&P', data.metrics.rating_sp.value || 'NR', rawStr(data.metrics.rating_sp.value))} />
            <Metric label="Avg Rating Moody's" value={data.metrics.rating_moodys.value || 'NR'} meta={data.metrics.rating_moodys}
              {...fieldHandlers("Avg Rating Moody's", data.metrics.rating_moodys.value || 'NR', rawStr(data.metrics.rating_moodys.value))} />
          </section>

          {data.performance && (
            <section style={{ display: 'flex', gap: 24, marginTop: 28, flexWrap: 'wrap' }}>
              <Metric label="Performance YTD" value={fmtPct(data.performance.year_to_date)}
                {...fieldHandlers('Performance YTD', fmtPct(data.performance.year_to_date), rawStr(data.performance.year_to_date))} />
              <Metric label="Performance ITD" value={fmtPct(data.performance.inception_to_date)}
                {...fieldHandlers('Performance ITD', fmtPct(data.performance.inception_to_date), rawStr(data.performance.inception_to_date))} />
            </section>
          )}
        </>
      )}
    </div>
  );
}
